import React, { useState, useRef, useEffect } from 'react';

export const topics = [
  'AI Agentic Solutions',
  'Software Development',
  'Workflow Automation',
  'Data Analytics',
  'Hire Developers',
  'Something else',
];

const emptyForm = { name: '', email: '', topic: '', subject: '', message: '' };

type Props = {
  presetTopic?: { value: string; at: number };
};

const Contact: React.FC<Props> = ({ presetTopic }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 110);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (presetTopic) {
      setSubmitted(false);
      setForm(p => ({ ...p, topic: presetTopic.value }));
    }
  }, [presetTopic]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(p => ({ ...p, [e.target.name]: '' }));
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Please add your name';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = 'Please add a valid email';
    if (!form.message.trim()) next.message = 'Please tell us a little about it';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const { name, email, topic, subject, message } = form;
    const mailSubject = subject || (topic ? `${topic}: message from ${name}` : `Message from ${name}`);
    const body = `Name: ${name}\nEmail: ${email}${topic ? `\nLooking for: ${topic}` : ''}\n\n${message}`;
    window.location.href = `mailto:team@slatewing.com?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <section className="contact" id="contact" ref={ref}>
      <div className="container">
        <div className="contact-inner">
          <div className="contact-info">
            <div className="reveal">
              <span className="section-label">Contact</span>
              <h2>Let's build<br /><span className="gt">something great</span></h2>
            </div>
            <p className="reveal d1">
              Have something specific in mind, or just want to talk through
              an idea? Drop us a message. We read every one and reply quickly.
            </p>
            <div className="contact-details">
              {[
                {
                  label: 'Office',
                  value: <>210 BBC, Nr. Savaliya Circle<br />Yogichowk, Surat<br />Gujarat, India</>,
                  icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
                  delay: 'd1',
                },
                {
                  label: 'Phone',
                  value: <a href="tel:+917359005500" style={{ color: 'inherit', textDecoration: 'none' }}>+91 73590 05500</a>,
                  icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6 6l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
                  delay: 'd2',
                },
                {
                  label: 'Email',
                  value: <a href="mailto:team@slatewing.com" style={{ color: 'inherit', textDecoration: 'none' }}>team@slatewing.com</a>,
                  icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>,
                  delay: 'd3',
                },
              ].map(item => (
                <div className={`contact-item reveal ${item.delay}`} key={item.label}>
                  <div className="ci-icon">{item.icon}</div>
                  <div className="ci-body">
                    <div className="ci-label">{item.label}</div>
                    <div className="ci-value">{item.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="contact-form reveal d2" style={{ scrollMarginTop: 100 }} id="contact-form">
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '48px 0' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'var(--gradient)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                  boxShadow: '0 8px 32px color-mix(in srgb, var(--brand) 35%, transparent)',
                  fontSize: '1.4rem', color: 'var(--bg)', fontWeight: 700,
                }}>✓</div>
                <h3 style={{ marginBottom: 10 }}>Almost done</h3>
                <p>
                  Your email app should have opened with the message ready.
                  Hit send there and it reaches us. If nothing opened, write to
                  us directly at <a href="mailto:team@slatewing.com" style={{ color: 'var(--brand)' }}>team@slatewing.com</a>.
                </p>
                <button className="btn btn-ghost" style={{ marginTop: 24 }} onClick={() => { setSubmitted(false); setForm(emptyForm); }}>
                  Write another message
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="c-name">Name</label>
                    <input id="c-name" name="name" type="text" className={`form-input${errors.name ? ' has-error' : ''}`} placeholder="Your name" value={form.name} onChange={onChange} required />
                    {errors.name && <span className="form-error">{errors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="c-email">Email</label>
                    <input id="c-email" name="email" type="email" className={`form-input${errors.email ? ' has-error' : ''}`} placeholder="you@company.com" value={form.email} onChange={onChange} required />
                    {errors.email && <span className="form-error">{errors.email}</span>}
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="c-topic">Looking for</label>
                    <select id="c-topic" name="topic" className="form-input form-select" value={form.topic} onChange={onChange}>
                      <option value="">Pick one</option>
                      {topics.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="c-subject">Subject</label>
                    <input id="c-subject" name="subject" type="text" className="form-input" placeholder="What's this about?" value={form.subject} onChange={onChange} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="c-message">Message</label>
                  <textarea id="c-message" name="message" className={`form-input${errors.message ? ' has-error' : ''}`} placeholder="Tell us about your project..." value={form.message} onChange={onChange} required />
                  {errors.message && <span className="form-error">{errors.message}</span>}
                </div>
                <button type="submit" className="btn btn-primary form-submit">
                  Send Message
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
